import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLiveRefresh } from '../hooks/useLiveRefresh';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios to send cookies
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Droits mis à jour par l'administration : rechargement immédiat via WebSocket (+ repli toutes les 60 s) sans reconnexion
  useLiveRefresh((evt) => { if (user) checkAuth(); if (user && evt?.type === 'permissions_update') toast.info('Vos droits ont été mis à jour.'); }, { types: ['permissions_update'], intervalMs: 60000, enabled: !!user });

  // Role changes are stored in MongoDB (not in the JWT): refresh the cached user on 403 and when the tab regains focus
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        if (error?.response?.status === 403 && !String(error.config?.url || '').includes('/auth/')) {
          try {
            const me = await axios.get(`${API}/auth/me`);
            setUser((prev) => {
              if (prev && prev.role !== me.data.role) {
                toast.info('Vos droits ont été mis à jour. Veuillez réessayer.');
              } else {
                toast.error('Accès refusé avec votre rôle actuel. Reconnectez-vous pour actualiser votre session.');
              }
              return me.data;
            });
          } catch (e) {
            setUser(false);
          }
        }
        return Promise.reject(error);
      },
    );
    const onVisible = () => { if (document.visibilityState === 'visible') checkAuth(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      axios.interceptors.response.eject(interceptor);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, hasPermission: (p) => !!user?.permissions?.includes(p) }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
